import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import type { MfaOtp } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import FormLabel from "~/components/login/formLabel/FormLabel";
import FormTitle from "~/components/login/formTitle/FormTitle";
import Button from "~/components/ui/button/Button";
import FormBaseErrors from "~/components/ui/formBaseErrors/FormBaseErrors";
import Input from "~/components/ui/input/Input";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useAuth } from "~/contexts/AuthProvider";
import labels from "~/helpers/labels";
import { tryCatchParsed } from "~/helpers/tryCatch";
import { useFormErrors } from "~/hooks/useFormErrors";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import styles from "./Login.module.scss";

// Server accepts backup codes up to this length (incl. any separator format).
// Centralised so the input cap and the validation message agree.
const BACKUP_CODE_MAX_LENGTH = 32;
const TOTP_CODE_LENGTH = 6;

const VerifyMfaPage = () => {
  usePageTitle(PAGE_TITLES.twoFactorAuthentication);

  const navigate = useNavigate();
  const { updateSession, clearAuth } = useAuth();

  const [useBackupCode, setUseBackupCode] = useState(false);

  // Cross-tab MFA sync is handled centrally in AuthProvider via the
  // `SESSION_UPDATED_AT_STORAGE_KEY` storage-event marker: when MFA verify
  // lands in any tab, every other tab on the same token invalidates the
  // session + authUser queries, and AppGate's symmetric redirect away from
  // /verify-mfa fires automatically. No page-local focus / visibilitychange
  // heuristics required.

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm<MfaOtp>();
  const { baseErrors, applyServerErrors, clearBaseErrors } =
    useFormErrors(setError);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: MfaOtp) => apiClient.mfa.verifyCreate(data),
  });

  const onSubmit: SubmitHandler<MfaOtp> = async (data) => {
    const result = await tryCatchParsed(
      mutateAsync({ otp_attempt: data.otp_attempt.trim() }),
    );

    if (result.error) {
      applyServerErrors(result.error);

      return;
    }

    // `/mfa/verify` does NOT rotate the token — it returns a fresh
    // `current_session` with `mfa_required: false`. Prime the session cache
    // directly; AppGate's synchronous gate kicks us off /verify-mfa on the
    // next render.
    updateSession(result.data.data.current_session);
  };

  const onSignOut = () => {
    clearAuth();
    navigate(routes.auth.login(), { replace: true });
  };

  const toggleBackup = () => {
    setUseBackupCode((prev) => !prev);
    reset({ otp_attempt: "" });
    // Errors are mode-specific ("Invalid authenticator code" doesn't apply to
    // backup codes and vice versa). Clear them so a stale message from the
    // previous mode does not confuse the user.
    clearBaseErrors();
  };

  const fieldLabel = useBackupCode ? "Backup code" : "Authentication code";
  const placeholder = useBackupCode ? "Enter a backup code" : "123456";
  const maxLength = useBackupCode ? BACKUP_CODE_MAX_LENGTH : TOTP_CODE_LENGTH;
  const inputMode = useBackupCode ? "text" : "numeric";
  const infoText = useBackupCode
    ? "Enter one of your backup codes."
    : `Enter the ${TOTP_CODE_LENGTH}-digit code from your authenticator app.`;

  return (
    <>
      <FormTitle title="Two-Factor Authentication" />
      <div className={styles.infoText}>{infoText}</div>
      <FormBaseErrors errors={baseErrors} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.inputsWrapper}>
          <FormLabel label={fieldLabel} />
          <Input
            autoFocus
            autoComplete="one-time-code"
            inputMode={inputMode}
            maxLength={maxLength}
            placeholder={placeholder}
            disabled={isPending}
            errormessage={errors.otp_attempt?.message ?? ""}
            {...register("otp_attempt", {
              required: labels.THIS_FIELD_CANNOT_BE_EMPTY,
              maxLength: {
                value: maxLength,
                message: `Code must be at most ${maxLength} characters`,
              },
            })}
          />
        </div>
        <Button
          fullWidth
          type="submit"
          disabled={isPending}
          withSpinner={isPending}
        >
          Verify
        </Button>
      </form>
      <div className={styles.footer}>
        <div className={styles.footerText}>
          {useBackupCode
            ? "Have your authenticator app?"
            : "Lost access to your authenticator app?"}
        </div>
        <button
          type="button"
          onClick={toggleBackup}
          className={styles.footerLink}
        >
          {useBackupCode ? "Use authenticator app" : "Use a backup code"}
        </button>
      </div>
      <div className={styles.footer}>
        <div className={styles.footerText}>Not your account?</div>
        <button type="button" onClick={onSignOut} className={styles.footerLink}>
          Sign out
        </button>
      </div>
    </>
  );
};

export default VerifyMfaPage;
